import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import api from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";

const SYMBOLS = { EUR: "€", USD: "$", GBP: "£" };

const GoalProgress = () => {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/donations/goal")
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data || !data.target || data.target <= 0) return null;

  const sym = SYMBOLS[data.currency] || data.currency;
  const pct = Math.min(100, Math.max(0, data.percentage || 0));
  const fmt = (n) =>
    n.toLocaleString(lang === "en" ? "en-US" : "fr-FR", { maximumFractionDigits: 0 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="max-w-3xl mx-auto mb-10 bg-white rounded-2xl border border-black/5 p-6 md:p-8 shadow-[0_4px_30px_-15px_rgba(0,43,73,0.15)]"
      data-testid="goal-progress"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-1.5">
            <TrendingUp size={14} />
            <span>{lang === "fr" ? "Objectif du mois" : "Monthly goal"}</span>
          </div>
          <div className="font-serif text-2xl md:text-3xl text-[#0A192F] font-medium" data-testid="goal-numbers">
            <span className="text-[#002B49] font-semibold">
              {sym} {fmt(data.raised)}
            </span>{" "}
            <span className="text-[#64748B] text-lg md:text-xl font-normal">
              {lang === "fr" ? "collectés sur" : "raised of"} {sym} {fmt(data.target)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-serif text-3xl md:text-4xl text-[#C5A059] font-semibold leading-none">
            {pct}%
          </div>
          <div className="text-xs text-[#64748B] mt-1">
            {data.count} {lang === "fr" ? `don${data.count > 1 ? "s" : ""}` : `donor${data.count > 1 ? "s" : ""}`}
          </div>
        </div>
      </div>

      <div className="relative h-3 bg-[#F3F0EA] rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: "easeOut", delay: 0.2 }}
          className="h-full rounded-full bg-gradient-to-r from-[#002B49] to-[#C5A059]"
          data-testid="goal-progress-bar"
        />
      </div>

      <p className="text-xs text-[#64748B] mt-4 leading-relaxed">
        {lang === "fr"
          ? "Aidez-nous à atteindre l'objectif ce mois-ci. Chaque don, ponctuel ou mensuel, compte."
          : "Help us reach this month's goal. Every gift — one-time or monthly — counts."}
      </p>
    </motion.div>
  );
};

export default GoalProgress;
